import { AlertCircle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { CorrectionPopover } from '@/components/products/product-spec-table'
import {
  correctionDetail,
  correctionErrorMessage,
  useProductCorrections,
  useSaveProductCorrections,
  type CorrectionField,
  type CorrectionRefresh,
  type TextCorrection,
} from '@/api/product-corrections'
import { parseUTCDate } from '@/lib/utils'

function shortDateTime(value: string) {
  return parseUTCDate(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Rewriting the product's text and photos and updating the store, said quietly. */
export function RefreshStatus({ refresh }: { refresh: CorrectionRefresh | null | undefined }) {
  if (!refresh) return null
  const active = refresh.status === 'queued' || refresh.status === 'running'
  return (
    <p role="status" className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      {active ? (
        <>
          <Loader2 className="size-3 shrink-0 animate-spin" aria-hidden="true" />
          <span className="truncate">
            {refresh.status === 'queued' ? 'Store update queued' : 'Updating the store'}
          </span>
        </>
      ) : refresh.status === 'failed' ? (
        <>
          <AlertCircle className="size-3 shrink-0 text-destructive" aria-hidden="true" />
          <span className="truncate text-destructive">Store update failed. Save again to retry.</span>
        </>
      ) : (
        <span className="truncate">
          Store updated {shortDateTime(refresh.finished_at ?? refresh.requested_at)}
        </span>
      )}
    </p>
  )
}

/** One listing value: click to edit; a staff value says who corrected it, with when, why and Undo behind that. */
function TextCorrectionCell({
  productId,
  field,
  label,
  value,
  correction,
  editable,
  multiline = false,
  hint,
  emptyText = 'Not set',
}: {
  productId: string
  field: Extract<CorrectionField, 'title' | 'specifications' | 'note'>
  label: string
  value: string | null | undefined
  correction: TextCorrection | null | undefined
  editable: boolean
  multiline?: boolean
  hint?: string
  emptyText?: string
}) {
  const save = useSaveProductCorrections(productId)
  const onError = (error: unknown) =>
    toast.error(correctionErrorMessage(error, 'Could not save the correction'))
  const confirm = (raw: string) => {
    const text = raw.trim()
    // Emptying a corrected value is the same as undoing it.
    if (!text) {
      if (correction) save.mutate({ release: [field] }, { onError })
      return
    }
    save.mutate({ [field]: text }, { onError })
  }

  return (
    <div className="border-b px-4 py-3 last:border-b-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      {editable ? (
        <POInlineEditableField
          value={value ?? ''}
          placeholder={emptyText}
          kind={multiline ? 'textarea' : 'text'}
          ariaLabel={`Edit ${label.toLowerCase()}`}
          title={`Edit ${label.toLowerCase()}`}
          pending={save.isPending}
          buttonClassName="-ml-2 text-left text-sm"
          showEditIcon
          allowWrap
          onConfirm={confirm}
        />
      ) : (
        <p className="whitespace-pre-wrap py-1.5 text-sm">{value || emptyText}</p>
      )}
      {hint && !value && <p className="text-xs text-muted-foreground">{hint}</p>}
      {correction && (
        <div className="-ml-2">
          <CorrectionPopover
            name={label}
            label={`Corrected by ${correction.by || 'staff'}`}
            detail={correctionDetail(correction.by, correction.at, correction.reason)}
            was={null}
            pending={save.isPending}
            onUndo={(close) => save.mutate({ release: [field] }, { onSuccess: close, onError })}
          />
        </div>
      )}
    </div>
  )
}

/** The store title, the AutoQuotes spec text and the note every AI writer follows; staff edits win until undone. */
export function ProductCorrectionsSection({
  productId,
  fallbackTitle,
  fallbackSpecifications,
}: {
  productId: string
  /** Shown while the corrections load or if they cannot. */
  fallbackTitle?: string | null
  fallbackSpecifications?: string | null
}) {
  const { data, isLoading, isError, refetch } = useProductCorrections(productId)

  return (
    <section
      aria-labelledby="product-listing"
      className="overflow-hidden rounded-xl border bg-card"
    >
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <h2 id="product-listing" className="shrink-0 text-sm font-semibold">
          Store listing
        </h2>
        <RefreshStatus refresh={data?.refresh} />
      </div>
      {isLoading ? (
        <div className="space-y-3 p-4">
          <Skeleton className="h-10" />
          <Skeleton className="h-16" />
        </div>
      ) : isError || !data ? (
        <div className="flex items-center gap-3 px-4 py-3 text-sm">
          <span className="text-destructive">Could not load the store listing.</span>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <div>
          <TextCorrectionCell
            productId={productId}
            field="title"
            label="Title"
            value={data.current_title ?? fallbackTitle}
            correction={data.title}
            editable
          />
          <TextCorrectionCell
            productId={productId}
            field="specifications"
            label="Specifications"
            value={data.current_specifications ?? fallbackSpecifications}
            correction={data.specifications}
            editable={data.specifications_editable}
            multiline
          />
          <TextCorrectionCell
            productId={productId}
            field="note"
            label="Correction note"
            value={data.note?.value}
            correction={data.note}
            editable
            multiline
            hint="Facts every AI writer follows over the spec sheet."
            emptyText="None"
          />
        </div>
      )}
    </section>
  )
}
